import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Alert,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { Title } from 'react-admin';
import QRScanner from './QRScanner';
import { getApiUrl } from '../utils/apiUrl';

const apiUrl = getApiUrl();

interface Service {
    uuid: string;
    name: string;
    description: string | null;
    category: string | null;
}

interface ServiceSelection {
    service_uuid: string;
    relationship_type: 'installed' | 'outsourced' | 'renter_responsibility';
}

interface FormData {
    homeUuid: string;
    email: string;
    fullName: string;
    phone: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    signColor: 'black' | 'white';
    services: ServiceSelection[];
}

const steps = ['Scan QR Code', 'Homeowner Information', 'Home Address', 'Services (Optional)', 'Review & Submit'];

const FieldSignUp: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState<FormData>({
        homeUuid: '',
        email: '',
        fullName: '',
        phone: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        signColor: 'black',
        services: []
    });
    const [uuidValid, setUuidValid] = useState<boolean | null>(null);
    const [uuidValidating, setUuidValidating] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [tempPassword, setTempPassword] = useState('');
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [availableServices, setAvailableServices] = useState<Service[]>([]);
    const [servicesLoading, setServicesLoading] = useState(false);

    const getAuthToken = () => localStorage.getItem('admin_token');

    const parseErrorResponse = async (response: Response, fallback: string) => {
        try {
            const data = await response.json();
            return data?.detail || fallback;
        } catch {
            return `${fallback} (status ${response.status})`;
        }
    };

    // Load available services on component mount
    React.useEffect(() => {
        const loadServices = async () => {
            setServicesLoading(true);
            try {
                const token = getAuthToken();
                const response = await fetch(`${apiUrl}/api/v1/field-signup/services`, {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setAvailableServices(data.services || []);
                }
            } catch (err: any) {
                console.error('Failed to load services:', err);
            } finally {
                setServicesLoading(false);
            }
        };

        loadServices();
    }, []);

    const validateUuid = async (uuid: string) => {
        if (!uuid) {
            setUuidValid(null);
            return;
        }

        setUuidValidating(true);
        setError('');

        try {
            const token = getAuthToken();
            const endpoint = `${apiUrl}/api/v1/field-signup/validate-uuid/${uuid}`;
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                }
            });

            if (response.ok) {
                setUuidValid(true);
            } else {
                const fallbackMessage =
                    response.status === 409
                        ? 'UUID is already in use'
                        : `UUID validation request failed (HTTP ${response.status})`;
                const parsedMessage = await parseErrorResponse(response, fallbackMessage);
                setUuidValid(false);
                setError(parsedMessage);
            }
        } catch (err: any) {
            setUuidValid(false);
            setError(err.message || 'Failed to validate UUID');
        } finally {
            setUuidValidating(false);
        }
    };

    const handleUuidChange = (uuid: string) => {
        setFormData({ ...formData, homeUuid: uuid });
        setUuidValid(null);
        setError('');
    };

    const handleUuidBlur = () => {
        if (formData.homeUuid) {
            validateUuid(formData.homeUuid);
        }
    };

    const handleScanSuccess = (uuid: string) => {
        setFormData({ ...formData, homeUuid: uuid });
        validateUuid(uuid);
    };

    const toggleService = (serviceUuid: string, relationshipType: 'installed' | 'outsourced' | 'renter_responsibility') => {
        const existingIndex = formData.services.findIndex(s => s.service_uuid === serviceUuid);
        
        if (existingIndex >= 0) {
            // Service already selected, remove it
            const newServices = [...formData.services];
            newServices.splice(existingIndex, 1);
            setFormData({ ...formData, services: newServices });
        } else {
            // Add new service
            setFormData({
                ...formData,
                services: [...formData.services, { service_uuid: serviceUuid, relationship_type: relationshipType }]
            });
        }
    };

    const isServiceSelected = (serviceUuid: string) => {
        return formData.services.some(s => s.service_uuid === serviceUuid);
    };

    const getServiceRelationType = (serviceUuid: string) => {
        const service = formData.services.find(s => s.service_uuid === serviceUuid);
        return service?.relationship_type || 'installed';
    };

    const handleNext = () => {
        setError('');
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setError('');
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError('');

        try {
            const token = getAuthToken();
            
            // Step 1: Register the homeowner
            const registerEndpoint = `${apiUrl}/api/v1/field-signup/register`;
            const registerResponse = await fetch(registerEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    home_uuid: formData.homeUuid,
                    email: formData.email,
                    full_name: formData.fullName,
                    phone: formData.phone || null,
                    street_address: formData.streetAddress,
                    city: formData.city,
                    state: formData.state,
                    zip_code: formData.zipCode,
                    sign_color: formData.signColor,
                    services: formData.services.length > 0 ? formData.services : null
                })
            });

            if (!registerResponse.ok) {
                const parsedMessage = await parseErrorResponse(registerResponse, 'Registration failed');
                throw new Error(parsedMessage);
            }

            const registerData = await registerResponse.json();
            setTempPassword(registerData.temp_password);

            // Show the temporary password to the sales rep
            setPasswordDialogOpen(true);

            // Step 2: Create checkout session
            const checkoutEndpoint = `${apiUrl}/api/v1/field-signup/create-checkout-session`;
            const checkoutResponse = await fetch(checkoutEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    sign_order_uuid: registerData.sign_order_uuid,
                    success_url: `${window.location.origin}/#/field-signup?status=success`,
                    cancel_url: `${window.location.origin}/#/field-signup?status=cancelled`
                })
            });

            if (!checkoutResponse.ok) {
                const parsedMessage = await parseErrorResponse(checkoutResponse, 'Failed to create checkout session');
                throw new Error(parsedMessage);
            }

            const checkoutData = await checkoutResponse.json();

            // Redirect to Stripe checkout after user acknowledges the password
            // Store the checkout URL for later use
            (window as any).stripeCheckoutUrl = checkoutData.checkout_url;

        } catch (err: any) {
            setError(err.message || 'An error occurred during registration');
            setLoading(false);
        }
    };

    const handlePasswordDialogClose = () => {
        setPasswordDialogOpen(false);
        // Redirect to Stripe checkout
        const checkoutUrl = (window as any).stripeCheckoutUrl;
        if (checkoutUrl) {
            window.location.href = checkoutUrl;
        }
    };

    const canProceedFromStep = (step: number): boolean => {
        switch (step) {
            case 0:
                return uuidValid === true;
            case 1:
                return !!(formData.email && formData.fullName);
            case 2:
                return !!(formData.streetAddress && formData.city && formData.state && formData.zipCode);
            case 3:
                return true; // Services step is optional
            default:
                return true;
        }
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Scan or Enter Sign UUID
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Scan the QR code on the physical sign or manually enter the UUID
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => setScannerOpen(true)}
                            sx={{ mb: 3 }}
                            fullWidth
                        >
                            Open QR Scanner
                        </Button>

                        <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>
                            OR
                        </Typography>

                        <TextField
                            label="Sign UUID"
                            value={formData.homeUuid}
                            onChange={(e) => handleUuidChange(e.target.value)}
                            onBlur={handleUuidBlur}
                            fullWidth
                            margin="normal"
                            disabled={uuidValidating}
                            error={uuidValid === false}
                            helperText={
                                uuidValidating
                                    ? 'Validating...'
                                    : uuidValid === true
                                    ? '✓ UUID is available'
                                    : uuidValid === false
                                    ? error || 'UUID is already in use'
                                    : 'Enter the UUID from the sign'
                            }
                        />

                        <QRScanner
                            open={scannerOpen}
                            onScanSuccess={handleScanSuccess}
                            onScanError={(err) => setError(err)}
                            onClose={() => setScannerOpen(false)}
                        />
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Homeowner Information
                        </Typography>
                        <TextField
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            fullWidth
                            margin="normal"
                            required
                        />
                        <TextField
                            label="Full Name"
                            value={formData.fullName}
                            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                            fullWidth
                            margin="normal"
                            required
                        />
                        <TextField
                            label="Phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            fullWidth
                            margin="normal"
                        />
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Home Address
                        </Typography>
                        <TextField
                            label="Street Address"
                            value={formData.streetAddress}
                            onChange={(e) => setFormData({ ...formData, streetAddress: e.target.value })}
                            fullWidth
                            margin="normal"
                            required
                        />
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="City"
                                    value={formData.city}
                                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                    fullWidth
                                    margin="normal"
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <TextField
                                    label="State"
                                    value={formData.state}
                                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                                    fullWidth
                                    margin="normal"
                                    required
                                    inputProps={{ maxLength: 2 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <TextField
                                    label="ZIP Code"
                                    value={formData.zipCode}
                                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                                    fullWidth
                                    margin="normal"
                                    required
                                />
                            </Grid>
                        </Grid>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Sign Color</InputLabel>
                            <Select
                                value={formData.signColor}
                                onChange={(e) => setFormData({ ...formData, signColor: e.target.value as 'black' | 'white' })}
                                label="Sign Color"
                            >
                                <MenuItem value="black">Black</MenuItem>
                                <MenuItem value="white">White</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                );

            case 3:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Services (Optional)
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Select any services the homeowner already has. This step can be skipped.
                        </Typography>

                        {servicesLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : availableServices.length === 0 ? (
                            <Alert severity="info">No services available</Alert>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {availableServices.map((service) => {
                                    const selected = isServiceSelected(service.uuid);
                                    const relationType = getServiceRelationType(service.uuid);
                                    
                                    return (
                                        <Card 
                                            key={service.uuid}
                                            variant="outlined"
                                            sx={{
                                                border: selected ? '2px solid #1976d2' : '1px solid #424242',
                                                backgroundColor: selected ? '#37474f' : '#2e2e2e',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    boxShadow: selected ? '0 4px 12px rgba(25, 118, 210, 0.3)' : '0 2px 8px rgba(0,0,0,0.3)',
                                                    backgroundColor: selected ? '#455a64' : '#353535'
                                                }
                                            }}
                                        >
                                            <CardContent>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                    {selected && (
                                                        <Box sx={{ 
                                                            backgroundColor: '#1976d2', 
                                                            borderRadius: '50%', 
                                                            width: 24, 
                                                            height: 24,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0
                                                        }}>
                                                            <Typography sx={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>✓</Typography>
                                                        </Box>
                                                    )}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: selected ? '#64b5f6' : '#ffffff' }}>
                                                            {service.name}
                                                        </Typography>
                                                        {service.description && (
                                                            <Typography variant="body2" sx={{ mt: 0.5, color: '#b0bec5' }}>
                                                                {service.description}
                                                            </Typography>
                                                        )}
                                                        {service.category && (
                                                            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#90a4ae' }}>
                                                                Category: {service.category}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                
                                                {selected && (
                                                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#0d47a1', borderRadius: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#ffffff' }}>
                                                            Relationship Type
                                                        </Typography>
                                                        <FormControl fullWidth size="small">
                                                            <Select
                                                                value={relationType}
                                                                onChange={(e) => {
                                                                    // Remove and re-add with new type
                                                                    const newServices = formData.services.filter(s => s.service_uuid !== service.uuid);
                                                                    setFormData({
                                                                        ...formData,
                                                                        services: [...newServices, {
                                                                            service_uuid: service.uuid,
                                                                            relationship_type: e.target.value as any
                                                                        }]
                                                                    });
                                                                }}
                                                                sx={{ 
                                                                    backgroundColor: '#1565c0',
                                                                    color: '#ffffff',
                                                                    '& .MuiSelect-select': {
                                                                        fontWeight: 'bold',
                                                                        color: '#ffffff',
                                                                        fontSize: '15px'
                                                                    },
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#1976d2'
                                                                    },
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#2196f3'
                                                                    },
                                                                    '& .MuiSvgIcon-root': {
                                                                        color: '#ffffff'
                                                                    }
                                                                }}
                                                            >
                                                                <MenuItem value="installed" sx={{ fontWeight: 'bold' }}>Installed</MenuItem>
                                                                <MenuItem value="outsourced" sx={{ fontWeight: 'bold' }}>Outsourced</MenuItem>
                                                                <MenuItem value="renter_responsibility" sx={{ fontWeight: 'bold' }}>Renter Responsibility</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                )}
                                                
                                                <Button
                                                    variant={selected ? "outlined" : "contained"}
                                                    color={selected ? "error" : "primary"}
                                                    onClick={() => toggleService(service.uuid, 'installed')}
                                                    sx={{ mt: 2 }}
                                                    fullWidth
                                                >
                                                    {selected ? 'Remove Service' : 'Add Service'}
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                );

            case 4:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Review & Submit
                        </Typography>
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Sign UUID
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {formData.homeUuid}
                                </Typography>

                                <Typography variant="subtitle2" color="textSecondary">
                                    Homeowner
                                </Typography>
                                <Typography variant="body1">
                                    {formData.fullName}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    {formData.email}
                                    {formData.phone && ` • ${formData.phone}`}
                                </Typography>

                                <Typography variant="subtitle2" color="textSecondary">
                                    Address
                                </Typography>
                                <Typography variant="body1">
                                    {formData.streetAddress}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    {formData.city}, {formData.state} {formData.zipCode}
                                </Typography>

                                <Typography variant="subtitle2" color="textSecondary">
                                    Sign Color
                                </Typography>
                                <Typography variant="body1" sx={{ textTransform: 'capitalize', mb: 2 }}>
                                    {formData.signColor}
                                </Typography>

                                <Typography variant="subtitle2" color="textSecondary">
                                    Services
                                </Typography>
                                {formData.services.length > 0 ? (
                                    <Box sx={{ mt: 1 }}>
                                        {formData.services.map((service) => {
                                            const serviceInfo = availableServices.find(s => s.uuid === service.service_uuid);
                                            return (
                                                <Box key={service.service_uuid} sx={{ mb: 1 }}>
                                                    <Typography variant="body2">
                                                        • {serviceInfo?.name || 'Unknown Service'}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" sx={{ ml: 2 }}>
                                                        Type: {service.relationship_type.replace('_', ' ')}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="textSecondary">
                                        No services selected
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        <Alert severity="info" sx={{ mb: 2 }}>
                            After clicking "Register & Pay", you'll receive a temporary password to share with the homeowner.
                            Then you'll be redirected to Stripe to complete the payment.
                        </Alert>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 3 }, maxWidth: 800, mx: 'auto' }}>
            <Title title="Field Sign-Up" />
            <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
                Field Sign-Up
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: { xs: 2, sm: 4 } }}>
                Register a new homeowner in the field
            </Typography>

            <Stepper
                activeStep={activeStep}
                orientation={isMobile ? 'vertical' : 'horizontal'}
                sx={{ mb: { xs: 2, sm: 4 } }}
            >
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Card>
                <CardContent>
                    {renderStepContent(activeStep)}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                        >
                            Back
                        </Button>
                        <Box>
                            {activeStep === steps.length - 1 ? (
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={loading || !canProceedFromStep(activeStep)}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Register & Pay'}
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    disabled={!canProceedFromStep(activeStep)}
                                >
                                    Next
                                </Button>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Temporary Password Dialog */}
            <Dialog open={passwordDialogOpen} onClose={handlePasswordDialogClose}>
                <DialogTitle>Registration Successful!</DialogTitle>
                <DialogContent>
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Homeowner account created successfully
                    </Alert>
                    <Typography variant="body1" gutterBottom>
                        Please share this temporary password with the homeowner:
                    </Typography>
                    <Box
                        sx={{
                            p: 3,
                            bgcolor: '#1976d2',
                            borderRadius: 1,
                            textAlign: 'center',
                            my: 2,
                            border: '2px solid #1565c0'
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontFamily: 'monospace',
                                fontWeight: 'bold',
                                letterSpacing: 4,
                                color: '#ffffff',
                                mb: 2
                            }}
                        >
                            {tempPassword}
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => {
                                navigator.clipboard.writeText(tempPassword);
                                alert('Password copied to clipboard!');
                            }}
                            sx={{ 
                                backgroundColor: '#ffffff',
                                color: '#1976d2',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            Copy Password
                        </Button>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                        The homeowner can use this password to log in and change it later.
                        Click "Continue to Payment" to complete the sign order.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePasswordDialogClose} variant="contained" color="primary">
                        Continue to Payment
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FieldSignUp;
